import {
  ensureSession,
  fetchProgress,
  isBackendUnreachable,
  login,
  logout,
  mergeLocalProgress,
  submitRun,
  toProgressMap,
  updateDisplayName,
  upgradeAccount,
  type ApiProgressSnapshot,
  type ApiResult,
  type ApiUser,
  type RunSubmission,
} from './api';
import { isRandomLevel } from '../game/levelGenerator';
import {
  loadProgress,
  markCutsceneSeen,
  recordResult,
  saveProgress,
  saveSeenCutscenes,
  seenCutscenes,
  type ProgressMap,
} from './progress';
import { ensureRunnerName, saveRunnerName } from './runner';

export interface SyncState {
  readonly progress: ProgressMap;
  readonly online: boolean;
  /** The account behind the session; null while offline. */
  readonly user: ApiUser | null;
}

export interface RecordedRun {
  readonly progress: ProgressMap;
  readonly stars: number;
  readonly score: number;
  readonly unlockedAchievements: readonly string[];
  readonly online: boolean;
}

export async function initSync(): Promise<SyncState> {
  const local = loadProgress();
  const runnerName = ensureRunnerName();

  const session = await ensureSession(runnerName).catch(() => null);
  if (session === null || !session.ok) return { progress: local, online: false, user: null };
  const user = await adoptRunnerName(session.value, runnerName);

  const merged = await mergeLocalProgress(local, [...seenCutscenes()]).catch(() => null);
  if (merged === null || !merged.ok) return { progress: local, online: false, user: null };

  return { progress: adoptSnapshot(merged.value), online: true, user };
}

/** The account's copy replaces this device's: progress and story beats alike. */
function adoptSnapshot(snapshot: ApiProgressSnapshot): ProgressMap {
  const progress = toProgressMap(snapshot.progress);
  saveProgress(progress);
  saveSeenCutscenes(snapshot.cutscenes);
  return progress;
}

/** The account's name wins; an account that has none takes this device's name. */
async function adoptRunnerName(user: ApiUser, localName: string): Promise<ApiUser> {
  if (user.displayName) {
    saveRunnerName(user.displayName);
    return user;
  }
  const renamed = await updateDisplayName(localName).catch(() => null);
  return renamed?.ok ? renamed.value : { ...user, displayName: localName };
}

/** Keeps the name on this device and, with a session, on the account. */
export async function renameRunner(name: string, state: SyncState): Promise<ApiResult<SyncState>> {
  saveRunnerName(name);
  if (state.user === null) return { ok: true, value: state };

  const result = await updateDisplayName(name).catch(() => null);
  if (result === null) {
    return { ok: false, reason: 'offline', message: 'The server could not be reached.' };
  }
  if (!result.ok) return result;
  return { ok: true, value: { ...state, user: result.value } };
}

export async function refreshProgress(fallback: SyncState): Promise<SyncState> {
  if (isBackendUnreachable()) return { ...fallback, online: false };

  const remote = await fetchProgress().catch(() => null);
  if (remote === null || !remote.ok) return { ...fallback, online: false };

  return { ...fallback, progress: adoptSnapshot(remote.value), online: true };
}

/** Remembers a story beat on this device and, with a session, on the account. */
export async function recordCutsceneSeen(cutsceneId: string): Promise<void> {
  markCutsceneSeen(cutsceneId);
  if (isBackendUnreachable()) return;
  await mergeLocalProgress({}, [cutsceneId]).catch(() => null);
}

/** Turns the guest session into a registered account; progress stays where it is. */
export async function registerAccount(
  email: string,
  password: string,
): Promise<ApiResult<ApiUser>> {
  return upgradeAccount(email, password).catch(() => ({
    ok: false as const,
    reason: 'offline' as const,
    message: 'The server could not be reached.',
  }));
}

/**
 * Signs into an existing account. Whatever this device has played as a guest
 * is merged into that account, so nothing earned before signing in is lost.
 */
export async function signIn(email: string, password: string): Promise<ApiResult<SyncState>> {
  const result = await login(email, password).catch(() => null);
  if (result === null) {
    return { ok: false, reason: 'offline', message: 'The server could not be reached.' };
  }
  if (!result.ok) return result;
  return { ok: true, value: await initSync() };
}

/** Ends the session and forgets this device's copy; the account keeps everything. */
export async function signOut(): Promise<SyncState> {
  await logout().catch(() => undefined);
  saveProgress({});
  saveSeenCutscenes([]);
  saveRunnerName(null);
  return initSync();
}

/**
 * The server recomputes score and stars from the submitted edges, so its answer
 * wins whenever it is reachable; the local store is only the offline fallback.
 */
export async function recordRun(
  local: ProgressMap,
  submission: RunSubmission,
  localScore: number,
  localStars: number,
): Promise<RecordedRun> {
  const remember = (stars: number, score: number): ProgressMap =>
    isRandomLevel(submission.levelId)
      ? local
      : recordResult(local, submission.levelId, stars, score);

  const offlineResult = (): RecordedRun => ({
    progress: remember(localStars, localScore),
    stars: localStars,
    score: localScore,
    unlockedAchievements: [],
    online: false,
  });

  if (isBackendUnreachable()) return offlineResult();

  const result = await submitRun(submission).catch(() => null);
  if (result === null || !result.ok) return offlineResult();

  return {
    progress: remember(result.value.run.stars, result.value.run.score),
    stars: result.value.run.stars,
    score: result.value.run.score,
    unlockedAchievements: result.value.unlockedAchievements ?? [],
    online: true,
  };
}
